// Libraries
import React, { useEffect, useState, useContext } from 'react';
import { FullStory } from '@fullstory/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

// Components
import UserScriptList from '../../components/UserScriptList.js';
import PaymentDue from '../../components/Modals/Subscription/paymentDue.js';
import UserCreditBar from './userCreditBar.js';
import Icon from '../../components/Elements/Icon.js';
import UpgradeMembership from '../../components/Modals/Subscription/upgradeMembership.js';

// Services
import scriptService from '../../services/scriptService.js';
import UserService from '../../services/userService.js';
import subscriptionService from '../../services/subscriptionService.js';

// Set User Context
import { UserContext } from '../../context/User';
import SessionContext from '../../context/Session';

// Construct Component
const WelcomePage = () => {
  const { user } = useContext(UserContext);
  const { isMobile, visitedWelcome, setVisitedWelcome } = useContext(SessionContext);
  const [scripts, setScripts] = useState([]);
  const [paymentDueIsOpen, setPaymentDueIsOpen] = useState(false);
  //const [isFreeTrialOpen, setIsFreeTrialOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleDeleteScript = (scriptId) => {
    UserService.addActionLog('Delete Script', scriptId, 'script');
    FullStory('trackEvent', {
      name: 'DeleteScriptMyScripts',
    });

    setScripts((scripts) => scripts.filter((s) => s.id !== scriptId));
  };

  const checkNewUser = async (currentDate, createdDate) => {
    const timeDifference = currentDate - createdDate;
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
    if (!visitedWelcome && user) {
      setVisitedWelcome(true);
      if (Math.abs(timeDifference) <= 604800000) {
        let aboutUs = await UserService.getUserAboutUs(user.id);
        // Only redirect when the API responded successfully but about_us isn't set.
        // A null response means an API/network error — don't redirect in that case.
        if (aboutUs !== null && !aboutUs.about_us) {
          window.location.href = `/app/newUserWelcome`;
        }
      }
    }
    return;
  };

  const updateScript = async (script) => {
    setScripts((scripts) => scripts.map((s) => (s.id === script.id ? script : s)));
  };

  const loadScripts = async () => {
    const userScripts = await scriptService.getScripts();
    setScripts(userScripts);
  };

  useEffect(() => {
    async function setup() {
      try {
        if (!user || user.id === undefined) return;

        const createdAtDate = new Date(user.created_at);
        const currentDate = new Date();
        checkNewUser(currentDate, createdAtDate);

        UserService.addActionLog('View Script List');

        await loadScripts();

        setLoading(false);

        if (user && user.invoiceDue && user.invoiceDue.length > 0) {
          setPaymentDueIsOpen(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
    if (user && user.id) {
      setup();
    }
  }, [user]);

  function closeMembershipModal() {
    setModalIsOpen(false);
  }

  const handleScriptUpload = () => {
    UserService.addActionLog('New Project ', null, null, 'From View Scripts');
    FullStory('trackEvent', {
      name: 'InitiateNewScriptMyScripts',
    });

    window.location.replace(`${window.location.origin}/app/newProject/1`);
  };

  return (
    <>
      {!loading && user && user.id ? (
        <div className="Page lightGrayBackground">
          <div>
            <div className="fullWidthContainer">
              <div className="scriptListContainer">
                <UserCreditBar
                  level={user?.level ?? 'basic'}
                  availableCredits={user?.availableCredits ?? null}
                />

                <div className="scriptListOuter">
                  {scripts && scripts.length > 0 ? (
                    <div className="scriptListGrid">
                      {scripts.map((script) => (
                        <UserScriptList
                          script={script}
                          updateScript={updateScript}
                          onDeleteScript={handleDeleteScript}
                          key={script.id}
                          refreshScripts={loadScripts}
                        />
                      ))}

                      <div
                        className={[
                          'projectCard',
                          'scriptListCardGridItemNewProject',
                          'scriptListCard',
                          isMobile ? 'scriptListCardMobile' : 'scriptListCardDesktop',
                        ].join(' ')}
                        style={{
                          height: '-webkit-fill-available',
                          width: '-webkit-fill-available',
                          padding: '0px',
                        }}
                        onClick={handleScriptUpload}
                      >
                        <div
                          className="projectCardBody placeholderCardBody"
                          style={{ padding: '70px 0 40px 0' }}
                        >
                          <a>
                            <FontAwesomeIcon icon={faPlus} className="placeholderCardIcon" />
                          </a>
                        </div>
                        <div className="scriptListTitle placeholderCardTitle">
                          Your Next Project
                        </div>
                        <div className="scriptListCreated placeholderCardSubtitle">
                          Start a new project and bring your vision to life
                        </div>
                        <div className="FullWidthFlex projectCardFooter"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="fullWidthContainer">
                      <div className="OnePortionFlex"></div>
                      <div className="TenPortionFlex centered noScriptsContent">
                        <div className="Padded large bold">
                          <Icon
                            icon="FilePointer"
                            color="Gray"
                            customClass="circleIcon noScriptsIcon"
                          />
                          <br />
                          You have no scripts yet
                        </div>
                        <div className="Padded subHeader">
                          You can generate a shotlist in a few minutes from a narrative script or
                          from a visual treatment in any language.
                        </div>
                        <div className="Padded">
                          <button
                            onClick={handleScriptUpload}
                            className="buttonClone noScriptsButton"
                          >
                            Get Started
                          </button>
                        </div>
                      </div>
                      <div className="OnePortionFlex"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <PaymentDue modalIsOpen={paymentDueIsOpen} setModalIsOpen={setPaymentDueIsOpen} />

      <UpgradeMembership
        isOpen={modalIsOpen}
        onRequestClose={closeMembershipModal}
        featureMsg={``}
      />
      {/*user && isFreeTrialOpen && (
        <FreeTrial isOpen={isFreeTrialOpen} onRequestClose={finishFreeTrial} userDetails={user} />
      )*/}
    </>
  );
};

export default WelcomePage;
